import { Undo2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

interface ViewportFilterButtonProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  isSidebarCollapsed: boolean;
  // Restore View mode (takes priority over Filter View)
  hasRestorableFilters?: boolean;
  onRestoreFilters?: () => void;
}

export function ViewportFilterButton({
  enabled,
  onChange,
  isSidebarCollapsed,
  hasRestorableFilters = false,
  onRestoreFilters,
}: ViewportFilterButtonProps) {
  const isMobile = useIsMobile();

  // Determine which mode we're in
  const isRestoreMode = hasRestorableFilters && onRestoreFilters;

  // Position styles
  const positionStyle: React.CSSProperties = isMobile
    ? {
        position: 'absolute',
        bottom: 'calc(var(--bottom-ui-offset) + 15px)', // Increased margin from map markers
        left: '50%',
        transform: 'translateX(-50%)',
        transition: 'bottom 0.2s ease-in-out',
        zIndex: 10, // Below drawer modals (z-15)
      }
    : {
        position: 'absolute',
        bottom: '30px', // Increased from 20px for better separation from markers
        left: isSidebarCollapsed
          ? 'calc(100px + (100vw - 100px) / 2)'
          : 'calc(320px + (100vw - 320px) / 2)', // Centered in remaining viewport
        transform: 'translateX(-50%)', // Center the element itself
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 10, // Below drawer modals (z-15)
      };

  return (
    <div style={positionStyle} className="viewport-filter-control-custom">
      <button
        className={cn('viewport-filter-button', isRestoreMode ? 'restore-mode' : (enabled && 'active'))}
        onClick={() => isRestoreMode ? onRestoreFilters() : onChange(!enabled)}
        type="button"
      >
        <span className="viewport-filter-icon">
          {isRestoreMode ? (
            // Restore View icon (undo/back arrow)
            <Undo2 width={16} height={16} />
          ) : enabled ? (
            // Filter View enabled (eye-off)
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            // Filter View disabled (eye)
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </span>
        <span className="viewport-filter-button-text">
          {isRestoreMode ? 'Restore View' : 'Filter View'}
        </span>
      </button>
    </div>
  );
}

